import React, { useEffect } from 'react';
import { useLocation, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, MapPin, Clock, Ticket, ArrowLeft, ArrowRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { eventInfo, images } from '../data/mock';
import { seoPages } from '../data/seoLandingPages';

const SEOLandingPage = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const page = seoPages[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page) {
    return <Navigate to="/" replace />;
  }

  const openTickets = () => window.open(eventInfo.ticketUrl, '_blank', 'noopener,noreferrer');
  const canonical = `https://www.alohatequilafest.com/${page.slug}`;

  return (
    <div className="min-h-screen bg-gray-950" data-testid={`seo-page-${page.slug}`}>
      <Helmet>
        <title>{page.metaTitle}</title>
        <meta name="description" content={page.metaDescription} />
        <meta name="keywords" content={page.keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={page.metaTitle} />
        <meta property="og:description" content={page.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: page.h1,
            description: page.metaDescription,
            url: canonical,
            mainEntityOfPage: canonical,
            author: { '@type': 'Organization', name: 'Aloha Tequila & Arts Festival' },
            publisher: {
              '@type': 'Organization',
              name: 'Aloha Tequila & Arts Festival',
              logo: { '@type': 'ImageObject', url: images.logo }
            },
            datePublished: '2026-02-20',
            dateModified: '2026-02-20',
            image: [images.logo],
            keywords: page.keywords
          })}
        </script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.alohatequilafest.com/' },
            { '@type': 'ListItem', position: 2, name: page.h1, item: canonical }
          ]
        })}</script>
      </Helmet>

      <Header />

      <main className="pt-28 sm:pt-32 pb-20">
        <article className="max-w-4xl mx-auto px-4">
          {/* Back link */}
          <Link
            to="/"
            data-testid="seo-back-home-link"
            className="inline-flex items-center gap-2 text-pink-400 hover:text-orange-400 transition-colors text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* H1 */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 leading-tight mb-6">
            {page.h1}
          </h1>

          {/* Quick event facts pill */}
          <div className="flex flex-wrap gap-3 mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30 text-white text-sm">
              <Calendar className="w-4 h-4 text-pink-400" />
              {eventInfo.date}
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/30 text-white text-sm">
              <Clock className="w-4 h-4 text-orange-400" />
              {eventInfo.time}
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-white text-sm">
              <MapPin className="w-4 h-4 text-yellow-400" />
              International Market Place, Waikiki
            </span>
          </div>

          {/* Intro */}
          <p className="text-white/85 text-lg sm:text-xl leading-relaxed mb-12">{page.intro}</p>

          {/* Sections */}
          <div className="space-y-10 mb-16">
            {page.sections.map((s, i) => (
              <section key={i} className="border-l-2 border-pink-500/40 pl-5 sm:pl-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{s.heading}</h2>
                <p className="text-white/75 leading-relaxed text-base sm:text-lg">{s.body}</p>
              </section>
            ))}
          </div>

          {/* CTA card */}
          <div className="bg-gradient-to-br from-pink-500/15 via-orange-500/15 to-yellow-500/10 border border-pink-500/40 rounded-3xl p-8 sm:p-10 text-center">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">{page.cta.headline}</h2>
            <p className="text-white/80 text-base sm:text-lg mb-6 max-w-2xl mx-auto">{page.cta.subhead}</p>
            <Button
              onClick={openTickets}
              data-testid="seo-cta-tickets-button"
              className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white text-lg font-bold rounded-full px-8 py-6 h-auto transition-all duration-300 hover:scale-105 inline-flex items-center gap-2"
            >
              <Ticket className="w-5 h-5" />
              Get Tickets via TicketSpice
            </Button>
          </div>

          {/* Internal cross-links */}
          <div className="grid sm:grid-cols-2 gap-4 mt-12">
            {Object.values(seoPages)
              .filter((p) => p.slug !== page.slug)
              .map((p) => (
                <Link
                  key={p.slug}
                  to={`/${p.slug}`}
                  data-testid={`seo-cross-link-${p.slug}`}
                  className="group bg-white/5 hover:bg-white/[0.08] border border-white/10 hover:border-pink-500/40 rounded-2xl p-5 transition-all duration-300"
                >
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Related</p>
                  <h3 className="text-white font-bold mb-2 group-hover:text-pink-400 transition-colors">
                    {p.h1}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-pink-400 text-sm">
                    Read more
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default SEOLandingPage;
